/**
 * @cfleet/customer - Main Application Component
 *
 * This is the main entry point for the customer portal.
 * Allows customers to place orders, track deliveries, view invoices,
 * and manage their relationship with dispatch organizations.
 *
 * Features:
 * - Authentication via @cfleet/auth (Better Auth)
 * - Protected routes requiring authentication
 * - Multi-tenant support (customers can belong to multiple organizations)
 * - React Router for navigation
 * - TanStack Query for data fetching
 */

import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, ProtectedRoute, useAuth, type AuthProviderType } from '@cfleet/auth';
import { useCustomerOrganizations, type CustomerOrganization } from '@cfleet/api-client';
import { useAuthConfig } from '@/hooks/useAuthConfig';

// Auth components
import SignIn from '@/components/SignIn';
import ForgotPassword from '@/components/ForgotPassword';
import ResetPassword from '@/components/ResetPassword';
import AcceptInvite from '@/components/AcceptInvite';
import VerifyEmail from '@/components/VerifyEmail';

// Portal components
import { Sidebar } from '@/components/Sidebar';
import { OrderList } from '@/components/OrderList';
import { CreateOrder } from '@/components/CreateOrder';
import { OrderDetails } from '@/components/OrderDetails';
import { InvoiceList } from '@/components/InvoiceList';
import { OrganizationList } from '@/components/OrganizationList';
import { OrganizationDetails } from '@/components/OrganizationDetails';
import { Settings } from '@/components/Settings';

// Types
import { ViewState } from '@/types';

// Create a client for TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// API configuration (will use environment variable in production)
// Uses proxy in development, direct URL in production
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Auth provider configuration
// - 'better-auth': Cookie-based auth via Better Auth sidecar (default, for all environments)
// - 'local': Direct API calls with bearer tokens (for unit tests only)
const AUTH_PROVIDER = (import.meta.env.VITE_AUTH_PROVIDER || 'better-auth') as AuthProviderType;
const BETTER_AUTH_URL = import.meta.env.VITE_BETTER_AUTH_URL || '';

/**
 * Customer Portal Component
 *
 * Main application layout with sidebar navigation and content area.
 * Organizations are now fetched via API hooks.
 * Orders are fetched via API hooks in OrderList component.
 * Invoices are fetched via API hooks in InvoiceList component.
 */
function CustomerPortal() {
  const { logout } = useAuth();
  const authConfig = useAuthConfig();

  // View state management
  const [currentView, setView] = useState<ViewState>('dashboard');

  // Fetch organizations from API for Sidebar and CreateOrder
  const { data: orgsData, isLoading: orgsLoading } = useCustomerOrganizations(
    { per_page: 50 },
    { authConfig }
  );

  const organizations = orgsData?.data ?? [];

  // Selection state
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [initialOrgForNewOrder, setInitialOrgForNewOrder] = useState<string>('');

  // Handlers
  const handleCreateOrderSuccess = () => {
    // Navigate back to dashboard after successful order creation
    setView('dashboard');
  };

  const handleStartNewOrder = (orgId: string) => {
    setInitialOrgForNewOrder(orgId);
    setView('create_order');
  };

  const handleViewOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
    setView('order_details');
  };

  const handleViewOrg = (orgId: string) => {
    setSelectedOrgId(orgId);
    setView('organization_details');
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Render content based on current view
  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return <OrderList onSelectOrder={handleViewOrder} />;

      case 'create_order':
        return (
          <CreateOrder
            onCancel={() => setView('dashboard')}
            onSubmit={handleCreateOrderSuccess}
            organizations={organizations}
            initialOrgId={initialOrgForNewOrder}
          />
        );

      case 'order_details':
        if (selectedOrderId) {
          return (
            <OrderDetails
              orderId={selectedOrderId}
              onBack={() => {
                setSelectedOrderId(null);
                setView('dashboard');
              }}
            />
          );
        }
        return null;

      case 'organizations':
        return <OrganizationList onSelectOrg={handleViewOrg} onNewOrder={handleStartNewOrder} />;

      case 'organization_details':
        if (selectedOrgId) {
          return (
            <OrganizationDetails
              orgId={selectedOrgId}
              onBack={() => {
                setSelectedOrgId(null);
                setView('organizations');
              }}
              onNewOrder={handleStartNewOrder}
            />
          );
        }
        return null;

      case 'invoices':
        return <InvoiceList />;

      case 'settings':
        return <Settings />;

      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen w-full bg-white text-zinc-900 font-sans selection:bg-blue-100">
      <Sidebar
        currentView={currentView}
        setView={setView}
        onSignOut={handleSignOut}
        organizations={organizations}
        organizationsLoading={orgsLoading}
        onNewOrder={handleStartNewOrder}
      />

      <main className="flex-1 min-w-0 relative bg-white">{renderContent()}</main>
    </div>
  );
}

/**
 * Main App Component
 *
 * Sets up providers and renders the application.
 * - BrowserRouter for client-side routing
 * - QueryClientProvider for TanStack Query data fetching
 * - AuthProvider for authentication (using customer appType)
 * - ProtectedRoute for route protection
 */
function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider
          apiBaseUrl={API_BASE_URL}
          appType="customer"
          authProvider={AUTH_PROVIDER}
          betterAuthUrl={BETTER_AUTH_URL || undefined}
        >
          <Routes>
            {/* Public auth routes */}
            <Route path="/login" element={<SignIn />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/verify-email" element={<VerifyEmail />} />

            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute loginPath="/login">
                  <CustomerPortal />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

export default App;
